import { faStar as faLightStar } from '@fortawesome/pro-light-svg-icons';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FavoriteType } from '@kcftech/prometheus-client';
import { Button } from 'components/button/Button';
import { useUser } from 'contexts/UserContext';
import {
    useCreateFavorite,
    useDeleteFavorite,
} from 'custom_hooks/data_hooks/favorites/useFavorites';
import * as React from 'react';
import { KeyboardEvent } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

export type ItemResponse = Readonly<{
    Id: string;
}>;

export type FavoriteButtonProps<TFavorite> = Readonly<{
    /**
     * The item to be favorited/unfavorited
     * (usually a response object, but only the item id is required)
     */
    favoriteItem: TFavorite;
    /**
     * The type of favorite the item is. (see {@link FavoriteType})
     */
    favoriteType: keyof typeof FavoriteType;
    className?: string;
}>;

const FavoriteIcon = styled(Button)`
    width: 26px;
    height: 26px;

    border: 1px solid transparent;
    transition: none;
    border-radius: 5px;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    &:disabled {
        background-color: transparent !important;
        border-color: transparent !important;
    }
    &:hover {
        background-color: ${tokens.background.subtle};
        border-color: ${tokens.border.default};
    }
    &:active {
        transition: none;
        border-color: ${tokens.misc.favorite};
    }
`;

export function FavoriteButton<TFavorite extends ItemResponse>({
    favoriteItem,
    favoriteType,
    className,
}: FavoriteButtonProps<TFavorite>) {
    const user = useUser();
    const isFavorite = user.favorites?.some(
        (favorite) => favorite.FavoriteId === favoriteItem.Id,
    );

    const {
        mutate: createFavorite,
        isPending: createFavoritePending,
    } = useCreateFavorite();

    const {
        mutate: deleteFavorite,
        isPending: deleteFavoritePending,
    } = useDeleteFavorite();

    const toggleFavorite = (event: React.MouseEvent | KeyboardEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (isFavorite) {
            deleteFavorite({ id: favoriteItem.Id, type: favoriteType });
        } else {
            createFavorite({ id: favoriteItem.Id, type: favoriteType });
        }
    };

    return (
        <FavoriteIcon
            title={
                isFavorite
                    ? `Remove ${favoriteType} from favorites`
                    : `Add ${favoriteType} to favorites`
            }
            disabled={createFavoritePending || deleteFavoritePending}
            onClick={(e: React.MouseEvent | KeyboardEvent) => toggleFavorite(e)}
            className={className}
        >
            <FontAwesomeIcon
                className="cursor-pointer"
                color={tokens.misc.favorite}
                icon={isFavorite ? faStar : faLightStar}
            />
        </FavoriteIcon>
    );
}
